import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { Line, SettingsLayout, SwitchItem } from '@components';
import { screens, ScreensNameType } from '@navigation';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { ToastService } from '@services';
import { parseApiError } from '@utils';
import { NotificationSettingsTypeEnum } from '@constants';
import { useNavOptionsSet } from '@hooks';

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.Notifications,
];

export const NotificationsScreen = observer(() => {
  const { t } = useTranslation();
  const viewer = useViewerModel();
  const settings = viewer.currentUser?.settings;

  useNavOptionsSet({ title: t('notifications.title') });

  async function toggleNotifications(
    notificationType: NotificationSettingsTypeEnum,
  ) {
    try {
      await settings?.toggleNotifications.run(notificationType);
    } catch (e) {
      ToastService.showError(parseApiError(e).message);
    }
  }

  function toggleNotifyAll() {
    toggleNotifications(NotificationSettingsTypeEnum.All);
  }

  function toggleNotifyNewTracks() {
    toggleNotifications(NotificationSettingsTypeEnum.NewTracks);
  }

  function toggleNotifyNewVideos() {
    toggleNotifications(NotificationSettingsTypeEnum.NewVideos);
  }

  function toggleNotifyNewMentions() {
    toggleNotifications(NotificationSettingsTypeEnum.NewMentions);
  }

  function toggleNotifyNewMessages() {
    toggleNotifications(NotificationSettingsTypeEnum.NewMessages);
  }

  return (
    <SettingsLayout
      title={t('notifications.title')}
      breadcrumbRoutes={BREADCRUMB_ROUTES}
    >
      <SwitchItem
        title={t('notifications.all')}
        value={viewer.currentUser?.settings.notifyAll}
        onValueChange={toggleNotifyAll}
      />
      <Line />

      <SwitchItem
        title={t('notifications.newTracks')}
        value={viewer.currentUser?.settings.notifyNewTracks}
        onValueChange={toggleNotifyNewTracks}
      />
      <Line />

      <SwitchItem
        title={t('notifications.newVideos')}
        value={viewer.currentUser?.settings.notifyNewVideos}
        onValueChange={toggleNotifyNewVideos}
      />
      <Line />

      <SwitchItem
        title={t('notifications.newMentions')}
        value={viewer.currentUser?.settings.notifyNewMentions}
        onValueChange={toggleNotifyNewMentions}
      />
      <Line />

      <SwitchItem
        title={t('notifications.newMessages')}
        value={viewer.currentUser?.settings.notifyNewMessages}
        onValueChange={toggleNotifyNewMessages}
      />
      <Line />
    </SettingsLayout>
  );
});
